import { checkPermissions } from "@elit-hair/elit-tools";
import {
    Button,
    Icon,
    InitialsAvatar,
    SearchBar,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Separator,
    Spinner,
} from "@elit-hair/elit-ui";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbChevronDown, TbUsers } from "react-icons/tb";
import { toast } from "sonner";
import z from "zod";

import { TeamMemberSelector } from "@/components/ui/team-member-selector";
import { PERMISSIONS } from "@/constants/permissions";
import { useSlackConnectedUserIds } from "@/hooks/queries/slack-integration.queries";
import {
    useAgentSeatAssignments,
    useCreateAgentSeatAssignment,
    useDeleteAgentSeatAssignment,
    useUpdateAgentSeatAssignment,
} from "@/hooks/queries/use-agent-seat-assignment.queries";
import { useUsersByIdsBatch } from "@/hooks/queries/user.queries";
import { cn } from "@/lib/utils";
import { useDashboardRole } from "@/pages/home/components/role-dashboard/hooks/use-dashboard-role";
import { useUserStatusContext } from "@/providers/general/user-status.provider";
import { useLatestOpenIncidents } from "@/queries/incidents.queries";
import { UserStatus } from "@/types/user-statuses.types";
import { isDevEnvironment } from "@/utils/is-dev-environment";
import { APP_ROUTES_TO } from "@/utils/routes";
import { getStatusCircleColor } from "@/utils/user-status";

import { AgentInformationPanel } from "./components/agent-information-panel";
import { AgentSettingsDialog } from "./components/agent-settings-dialog";
import {
    type AgentProps,
    OfficeScene,
    type OfficeSceneHandle,
    OfficeView,
    type SeatClickEvent,
    getSeatKey,
} from "./components/map";
import { defaultCables, defaultFloor, defaultLabels, defaultTables, defaultWalls } from "./components/map/definitions";
import { OfficeLegend } from "./components/office-legend";
import { type PickedSeat, SeatPickerDialog } from "./components/seat-picker-dialog";

// Incidents from this many hours back that are still open get an alert above the seat
const OPEN_INCIDENT_WINDOW_HOURS = 24;

export type AgentsInformation = {
    id: string;
    table: string;
    seat: number;
    status: UserStatus;
};

const OfficeSearchSchema = z.object({
    // Unknown values fall back to the default view instead of failing the route
    view: z.nativeEnum(OfficeView).optional().catch(undefined),
});

export const Route = createFileRoute("/management/office/")({
    validateSearch: zodValidator(OfficeSearchSchema),
    beforeLoad: () => {
        if (!isDevEnvironment()) {
            throw redirect({ to: APP_ROUTES_TO.MANAGEMENT_USERS });
        }
    },
    component: ManagementOffice,
    staticData: {
        config: {
            breadcrumb: {
                title: "office",
                to: APP_ROUTES_TO.MANAGEMENT_OFFICE,
            },
            title: "office",
        },
    },
});

function ManagementOffice() {
    const { t } = useTranslation();

    const sceneRef = useRef<OfficeSceneHandle>(null);
    const zoomIndicatorRef = useRef<HTMLSpanElement>(null);

    const [selectedAgent, setSelectedAgent] = useState<AgentProps | null>(null);
    const [selectedSeat, setSelectedSeat] = useState<{ tableId: string; seatNumber: number; agentId?: string } | null>(
        null
    );

    // What the seat colors show; Slack needs its own permission
    const { permissionCodes } = useDashboardRole();
    const canViewSlack = checkPermissions(permissionCodes, [PERMISSIONS.CALL_SLACK_CONNECTIONS_READ]);
    const navigate = Route.useNavigate();
    const search = Route.useSearch();
    const view = canViewSlack ? (search.view ?? OfficeView.CALL_STATUS) : OfficeView.CALL_STATUS;
    const handleViewChange = useCallback(
        (nextView: OfficeView) => {
            // The default view keeps the URL clean
            void navigate({
                search: (old) => ({ ...old, view: nextView === OfficeView.CALL_STATUS ? undefined : nextView }),
                replace: true,
            });
        },
        [navigate]
    );
    const { data: slackConnectedUserIds } = useSlackConnectedUserIds(view === OfficeView.SLACK);
    const { data: openIncidents } = useLatestOpenIncidents(OPEN_INCIDENT_WINDOW_HOURS);
    const openIncidentStartTimes = useMemo(
        () =>
            openIncidents &&
            new Map([...openIncidents].map(([userId, incident]) => [userId, Date.parse(incident.createdAt)])),
        [openIncidents]
    );

    // Modal state
    const [showSeatMemberPicker, setShowSeatMemberPicker] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [isAgentPanelOpen, setIsAgentPanelOpen] = useState(false);

    // Search state for unassigned users list
    const [unassignedUsersSearchQuery, setUnassignedUsersSearchQuery] = useState("");

    // Mutations
    const createAssignment = useCreateAgentSeatAssignment();
    const updateAssignment = useUpdateAgentSeatAssignment();
    const deleteAssignment = useDeleteAgentSeatAssignment();

    const { data: allAssignments = [] } = useAgentSeatAssignments();

    // Ignore assignments to tables or seats that are no longer on the plan
    const assignments = useMemo(() => {
        const tableSizes = new Map(defaultTables.map((table) => [table.id, table.size]));
        return allAssignments.filter((a) => a.seatNumber <= (tableSizes.get(a.tableId) ?? 0));
    }, [allAssignments]);

    // User statuses
    const { userStatuses } = useUserStatusContext();

    // Users that already have a seat (memoized)
    const assignedUserIds = useMemo(() => assignments.map((assignment) => assignment.agentId), [assignments]);

    const assignedUserIdsSet = useMemo(() => new Set(assignedUserIds), [assignedUserIds]);

    // Find users who have status but no seat assignment (memoized)
    const unassignedUsersWithStatus = useMemo(
        () => Object.keys(userStatuses).filter((userId) => !assignedUserIdsSet.has(userId)),
        [userStatuses, assignedUserIdsSet]
    );

    // Load minimal user info for unassigned users via auth-svc /user/batch
    const { data: unassignedUsersData = [], isLoading: isLoadingUnassignedUsers } =
        useUsersByIdsBatch(unassignedUsersWithStatus);

    // Apply client-side search over unassigned users
    const filteredUnassignedUsers = useMemo(() => {
        const query = unassignedUsersSearchQuery.trim().toLowerCase();
        if (!query) return unassignedUsersData;
        return unassignedUsersData.filter((user) => {
            const name = (user.name ?? "").toLowerCase();
            const email = (user.email ?? "").toLowerCase();
            return name.includes(query) || email.includes(query);
        });
    }, [unassignedUsersData, unassignedUsersSearchQuery]);

    // Toggle state for unassigned users section
    const [showUnassignedUsers, setShowUnassignedUsers] = useState(false);

    // Legend counts: agents per status, plus free seats for "unassigned"
    const statusCounts = useMemo(() => {
        const counts: Partial<Record<UserStatus, number>> = {};
        for (const status of Object.values(userStatuses)) counts[status] = (counts[status] ?? 0) + 1;
        return counts;
    }, [userStatuses]);
    const onlineAgents = Object.keys(userStatuses).length - (statusCounts[UserStatus.OFFLINE] ?? 0);
    const emptySeats = useMemo(
        () => defaultTables.reduce((total, table) => total + table.size, 0) - assignments.length,
        [assignments]
    );

    const seatedAgentsWithOpenIncidents = useMemo(
        () => (openIncidentStartTimes ? assignments.filter((a) => openIncidentStartTimes.has(a.agentId)).length : 0),
        [assignments, openIncidentStartTimes]
    );

    const slackCounts = useMemo(() => {
        if (!slackConnectedUserIds) return undefined;
        const connected = assignments.filter((a) => slackConnectedUserIds.has(a.agentId)).length;
        return { connected, notConnected: assignments.length - connected };
    }, [assignments, slackConnectedUserIds]);

    // Seat occupancy and live status, keyed by table and seat
    const seatInformation = useMemo(() => {
        const information: Record<string, AgentsInformation> = {};
        for (const assignment of assignments) {
            information[getSeatKey(assignment.tableId, assignment.seatNumber)] = {
                id: assignment.agentId,
                table: assignment.tableId,
                seat: assignment.seatNumber,
                status: userStatuses[assignment.agentId] || UserStatus.OFFLINE,
            };
        }
        return information;
    }, [assignments, userStatuses]);

    const selectedSeatKey = useMemo(() => {
        if (!selectedAgent?.userId) return undefined;
        const assignment = assignments.find((a) => a.agentId === selectedAgent.userId);
        return assignment ? getSeatKey(assignment.tableId, assignment.seatNumber) : undefined;
    }, [assignments, selectedAgent]);

    const handleAgentClick = useCallback((agent: AgentProps) => {
        setSelectedAgent(agent);
        setIsAgentPanelOpen(true);
    }, []);

    const handleSeatClick = useCallback((tableId: string, seatNumber: number, agentId?: string) => {
        setSelectedSeat({ tableId, seatNumber, agentId });
        if (!agentId) {
            setShowSeatMemberPicker(true);
        }
    }, []);

    const handleDeleteAssignment = useCallback(async () => {
        if (!selectedSeat) return false;
        return await deleteAssignment.mutateAsync({
            tableId: selectedSeat.tableId,
            seatNumber: selectedSeat.seatNumber,
        });
    }, [selectedSeat, deleteAssignment]);

    /** Puts a user on a seat, freeing any seat they had before. Returns whether it worked. */
    const assignUserToSeat = useCallback(
        async ({
            userId,
            seat,
        }: {
            userId: string;
            seat: { tableId: string; seatNumber: number; agentId?: string };
        }): Promise<boolean> => {
            try {
                const { tableId, seatNumber, agentId } = seat;

                // A person sits in one place only, so free their previous seat first
                const previousSeat = allAssignments.find(
                    (a) => a.agentId === userId && !(a.tableId === tableId && a.seatNumber === seatNumber)
                );
                if (previousSeat) {
                    await deleteAssignment.mutateAsync({
                        tableId: previousSeat.tableId,
                        seatNumber: previousSeat.seatNumber,
                    });
                }

                if (agentId) {
                    await updateAssignment.mutateAsync({ tableId, seatNumber, update: { agentId: userId } });
                } else {
                    await createAssignment.mutateAsync({ tableId, seatNumber, agentId: userId });
                }

                if (previousSeat) {
                    toast.info(
                        t("office_assign_seat_moved", { seat: `${previousSeat.tableId}-${previousSeat.seatNumber}` })
                    );
                }
                return true;
            } catch (error) {
                console.error("Error assigning agent to seat:", error);
                toast.error(t("something_went_wrong"));
                return false;
            }
        },
        [allAssignments, deleteAssignment, updateAssignment, createAssignment, t]
    );

    const handleAssignSeat = useCallback(
        async (userId: string) => {
            if (!selectedSeat) return;
            if (!(await assignUserToSeat({ userId, seat: selectedSeat }))) return;

            setShowSeatMemberPicker(false);
            setSelectedSeat(null);
            setSelectedAgent(null); // Close the info panel
        },
        [selectedSeat, assignUserToSeat]
    );

    // Person from the "unassigned users" list who is choosing a seat on the map
    const [seatPickerUser, setSeatPickerUser] = useState<{ id: string; name?: string | null; email: string } | null>(
        null
    );

    const handleSeatPicked = useCallback(
        async ({ tableId, seatNumber }: PickedSeat) => {
            if (!seatPickerUser) return;
            const user = seatPickerUser;
            setSeatPickerUser(null);

            if (await assignUserToSeat({ userId: user.id, seat: { tableId, seatNumber } })) {
                toast.success(
                    t("office_seat_assigned", { name: user.name || user.email, seat: `${tableId}-${seatNumber}` })
                );
            }
        },
        [seatPickerUser, assignUserToSeat, t]
    );

    const handleSceneSeatClick = useCallback(
        ({ tableId, agent }: SeatClickEvent) => {
            handleAgentClick(agent);
            handleSeatClick(tableId, agent.id, agent.userId);
        },
        [handleAgentClick, handleSeatClick]
    );

    // Update the indicator directly to avoid rerendering on every zoom step
    const handleZoomChange = useCallback((zoom: number) => {
        if (zoomIndicatorRef.current) {
            zoomIndicatorRef.current.textContent = `${Math.round(zoom * 100)}%`;
        }
    }, []);

    const resetView = useCallback(() => sceneRef.current?.resetView(), []);

    const handleOpenSettingsModal = useCallback(() => {
        setShowSettingsModal(true);
    }, []);

    return (
        <div className="flex flex-1 min-h-0 w-full">
            <div className="flex flex-1 min-h-0 w-full flex-col">
                <div className="relative isolate flex flex-1 min-h-0 w-full items-center justify-center overflow-hidden rounded-xl border border-border/60 bg-linear-to-br from-muted/50 via-muted/30 to-muted/10 shadow-inner select-none">
                    <div className="absolute top-3 right-3 z-20 flex shrink-0 items-center overflow-hidden rounded-xl border border-border/60 bg-background/80 shadow-lg backdrop-blur-lg">
                        {canViewSlack && (
                            <>
                                <Select value={view} onValueChange={(value) => handleViewChange(value as OfficeView)}>
                                    <SelectTrigger
                                        aria-label={t("view")}
                                        className="h-auto w-auto gap-2 rounded-none border-0 bg-transparent px-2.5 py-1.5 text-sm shadow-none sm:px-3"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={OfficeView.CALL_STATUS}>
                                            {t("office_view_call_status")}
                                        </SelectItem>
                                        <SelectItem value={OfficeView.SLACK}>{t("office_view_slack")}</SelectItem>
                                    </SelectContent>
                                </Select>
                                <div className="h-6 w-px bg-border/60" />
                            </>
                        )}
                        <div className="flex items-center gap-2 px-2.5 py-1.5 sm:px-3">
                            <div className="h-2 w-2 animate-pulse rounded-full bg-primary opacity-60"></div>
                            <span ref={zoomIndicatorRef} className="text-sm font-medium text-foreground/80">
                                100%
                            </span>
                        </div>
                        <div className="h-6 w-px bg-border/60" />
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={resetView}
                            className="h-auto rounded-none px-2.5 py-1.5 hover:bg-muted/80 sm:px-3"
                        >
                            <span className="hidden sm:inline">{t("reset_view")}</span>
                            <span className="sm:hidden">{t("reset")}</span>
                        </Button>
                    </div>
                    <OfficeScene
                        ref={sceneRef}
                        className="absolute inset-0"
                        walls={defaultWalls}
                        floor={defaultFloor}
                        view={view}
                        slackConnectedUserIds={slackConnectedUserIds}
                        incidentStartTimes={openIncidentStartTimes}
                        labels={defaultLabels}
                        tables={defaultTables}
                        cables={defaultCables}
                        seatInformation={seatInformation}
                        selectedSeatKey={selectedSeatKey}
                        onSeatClick={handleSceneSeatClick}
                        onZoomChange={handleZoomChange}
                    />

                    {/* Unassigned Users with Status Section */}
                    {unassignedUsersWithStatus.length > 0 && (
                        <div className="absolute left-2 top-3 z-10 max-w-[calc(100%-10.5rem)] overflow-hidden rounded-xl border border-border bg-background sm:max-w-[300px] sm:w-[300px]">
                            <Button
                                variant="ghost"
                                onClick={() => setShowUnassignedUsers(!showUnassignedUsers)}
                                className="flex h-auto w-full min-w-0 justify-between gap-2 rounded-none p-2 transition-all duration-200 hover:bg-muted/50"
                            >
                                <div className="flex min-w-0 items-center gap-2">
                                    <Icon icon={TbUsers} size={20} className="shrink-0" />
                                    <span className="truncate text-sm">
                                        {filteredUnassignedUsers.length} {t("unassigned_users")}
                                    </span>
                                </div>
                                <Icon
                                    icon={TbChevronDown}
                                    size={20}
                                    className={cn("shrink-0 transition-transform duration-200", {
                                        "-rotate-180": showUnassignedUsers,
                                    })}
                                />
                            </Button>

                            {showUnassignedUsers && (
                                <>
                                    <Separator />
                                    <div className="border-b bg-background p-2">
                                        <SearchBar
                                            value={unassignedUsersSearchQuery}
                                            onChange={setUnassignedUsersSearchQuery}
                                            placeholder={t("search_users")}
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="max-h-[min(300px,40vh)] overflow-y-auto md:max-h-[300px]">
                                        {isLoadingUnassignedUsers && filteredUnassignedUsers.length === 0 ? (
                                            <div className="flex items-center justify-center p-4">
                                                <Spinner size="sm" />
                                            </div>
                                        ) : filteredUnassignedUsers.length === 0 ? (
                                            <div className="text-center py-8 text-sm text-muted-foreground">
                                                {t("no_unassigned_users")}
                                            </div>
                                        ) : (
                                            <>
                                                {filteredUnassignedUsers.map((user) => {
                                                    const status = userStatuses[user.id] || UserStatus.OFFLINE;

                                                    // Convert status to translation key (e.g., "inCall" -> "in_call")
                                                    const statusKey = status
                                                        .replace(/([A-Z])/g, "_$1")
                                                        .toLowerCase()
                                                        .replace(/^_/, "");

                                                    return (
                                                        <div
                                                            role="button"
                                                            key={user.id}
                                                            onClick={() => setSeatPickerUser(user)}
                                                            className="group flex cursor-pointer items-center justify-between gap-2 border-b border-border p-2 transition-all duration-200 last:border-b-0 hover:bg-muted"
                                                        >
                                                            <div className="flex min-w-0 items-center gap-2">
                                                                <InitialsAvatar
                                                                    name={user.name || t("unknown")}
                                                                    size="sm"
                                                                />
                                                                <div className="flex min-w-0 flex-1 flex-col">
                                                                    <div className="truncate text-sm font-semibold">
                                                                        {user.name || t("unknown")}
                                                                    </div>
                                                                    {user.email && (
                                                                        <div className="truncate text-xs text-muted-foreground">
                                                                            {user.email}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex shrink-0 items-center gap-1.5">
                                                                <div
                                                                    className={`h-2.5 w-2.5 rounded-full ${getStatusCircleColor(status)}`}
                                                                />
                                                                <span className="hidden text-xs font-medium capitalize text-muted-foreground sm:inline">
                                                                    {t(statusKey)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    <OfficeLegend
                        view={view}
                        statusCounts={statusCounts}
                        onlineAgents={onlineAgents}
                        emptySeats={emptySeats}
                        slackCounts={slackCounts}
                        openIncidents={seatedAgentsWithOpenIncidents}
                    />

                    <AgentInformationPanel
                        isOpen={isAgentPanelOpen}
                        onOpenChange={setIsAgentPanelOpen}
                        assignments={assignments}
                        selectedAgent={selectedAgent}
                        selectedSeat={selectedSeat}
                        userStatuses={userStatuses}
                        setSelectedAgent={setSelectedAgent}
                        setSelectedSeat={setSelectedSeat}
                        setShowSeatMemberPicker={setShowSeatMemberPicker}
                        isLoading={false}
                        deleteAssignment={handleDeleteAssignment}
                        handleOpenSettingsModal={handleOpenSettingsModal}
                        openIncident={selectedAgent?.userId ? openIncidents?.get(selectedAgent.userId) : undefined}
                    />
                </div>
            </div>

            {/* Clicking a free seat (or reassigning one) opens the member picker straight away */}
            <TeamMemberSelector
                hideTrigger
                open={showSeatMemberPicker}
                onOpenChange={(open) => {
                    setShowSeatMemberPicker(open);
                    if (!open) setSelectedSeat(null);
                }}
                selectedMemberId={selectedSeat?.agentId ?? null}
                onMemberChange={(memberId) => {
                    if (memberId && memberId !== selectedSeat?.agentId) void handleAssignSeat(memberId);
                }}
                labels={{ resetSelection: "clear_selection" }}
            />

            <SeatPickerDialog
                open={!!seatPickerUser}
                onOpenChange={(open) => {
                    if (!open) setSeatPickerUser(null);
                }}
                title={t("office_seat_picker_title", {
                    name: seatPickerUser?.name || seatPickerUser?.email || t("unknown"),
                })}
                requestedFor={
                    seatPickerUser
                        ? { id: seatPickerUser.id, name: seatPickerUser.name || seatPickerUser.email }
                        : undefined
                }
                onSelect={handleSeatPicked}
            />

            <AgentSettingsDialog
                open={showSettingsModal}
                onOpenChange={setShowSettingsModal}
                userId={selectedAgent?.userId}
            />
        </div>
    );
}
